import { useState } from 'react';
import {
  Settings,
  Server,
  Shield,
  Globe,
  Bell,
  Database,
  Clock,
  Palette,
  Code2,
  Lock,
  Unlock,
  CheckCircle2,
  RotateCcw,
  Users,
  Network,
  MapPin,
  Building2,
  School,
  UserCheck,
  Trash2,
  Eye,
  EyeOff,
  HelpCircle,
  GraduationCap,
  Rocket,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuthStore } from '../../store/authStore';
import { useDevStore } from '../../store/useDevStore';

/**
 * Configurações do Sistema — parâmetros globais da plataforma e modo desenvolvedor.
 */
export function MasterConfigPage() {
  const user = useAuthStore((s) => s.user);
  const { settings, unlock, lock, updateSetting, resetDefaults } = useDevStore();

  const [activeTab, setActiveTab] = useState<'status' | 'dev'>('status');
  const [devPassword, setDevPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleUnlock = (e: React.FormEvent) => {
    e.preventDefault();
    if (!devPassword) {
      toast.error('Digite a senha de desenvolvedor.');
      return;
    }
    const ok = unlock(devPassword);
    if (ok) {
      toast.success('Modo Desenvolvedor desbloqueado com sucesso!');
      setDevPassword('');
    } else {
      toast.error('Senha de desenvolvedor incorreta!');
    }
  };

  const configs = [
    {
      title: 'Plataforma',
      icon: Globe,
      color: '#6366f1',
      items: [
        { label: 'Nome da Plataforma', value: 'Aluno Conectado', desc: 'Nome exibido no login e cabeçalhos' },
        { label: 'Versão', value: 'v2.0.0', desc: 'Versão atual do sistema' },
        { label: 'Ambiente', value: 'Produção', desc: 'Ambiente de execução ativo' },
        { label: 'URL Base', value: 'https://app.alunoconectado.com.br', desc: 'Endereço principal de acesso' },
      ],
    },
    {
      title: 'Segurança',
      icon: Shield,
      color: '#10b981',
      items: [
        { label: 'Sessão Única', value: 'Ativado', desc: 'Apenas uma sessão ativa por usuário' },
        { label: 'Expiração Token', value: '15 minutos', desc: 'Tempo de validade do access token' },
        { label: 'Refresh Token', value: '7 dias', desc: 'Tempo de validade do refresh token' },
        { label: 'Audit Log', value: 'Ativado', desc: 'Registro de todas as ações de admin' },
        { label: 'Fingerprint', value: 'Ativado', desc: 'Identificação de dispositivo por fingerprint' },
      ],
    },
    {
      title: 'Infraestrutura',
      icon: Server,
      color: '#f59e0b',
      items: [
        { label: 'Banco de Dados', value: 'PostgreSQL 16', desc: 'Banco relacional principal' },
        { label: 'Cache', value: 'Redis 7', desc: 'Cache de sessões e dados temporários' },
        { label: 'Backend', value: 'NestJS 11', desc: 'Framework da API REST' },
        { label: 'Frontend', value: 'React + Vite', desc: 'Interface web moderna' },
      ],
    },
    {
      title: 'Notificações',
      icon: Bell,
      color: '#3b82f6',
      items: [
        { label: 'Push Notifications', value: 'FCM (Firebase)', desc: 'Envio de notificações push para responsáveis' },
        { label: 'Email', value: 'Configurar', desc: 'Provedor de email transacional' },
        { label: 'SMS', value: 'Desativado', desc: 'Notificações por SMS (futuro)' },
      ],
    },
    {
      title: 'Dados & Backup',
      icon: Database,
      color: '#8b5cf6',
      items: [
        { label: 'Backup Automático', value: 'Diário 03:00', desc: 'Backup completo do banco de dados' },
        { label: 'Retenção', value: '30 dias', desc: 'Tempo de armazenamento dos backups' },
        { label: 'Soft Delete', value: 'Ativado', desc: 'Dados nunca são removidos fisicamente' },
      ],
    },
    {
      title: 'Operações',
      icon: Clock,
      color: '#0891b2',
      items: [
        { label: 'Timezone', value: 'America/Sao_Paulo', desc: 'Fuso horário padrão do sistema' },
        { label: 'Idioma', value: 'Português (BR)', desc: 'Idioma padrão da interface' },
        { label: 'Moeda', value: 'BRL (R$)', desc: 'Moeda para billing e relatórios' },
        { label: 'Ano Letivo', value: '2026', desc: 'Ano letivo corrente para matrículas' },
      ],
    },
  ];

  const cadastrosConfigs = [
    {
      key: 'allowDeleteUsuarios' as const,
      label: 'Usuários',
      description: 'Permitir ações de desativação/remoção de contas na tela de Usuários',
      icon: Users,
      color: '#6366f1',
    },
    {
      key: 'allowDeleteRedes' as const,
      label: 'Redes de Ensino',
      description: 'Permitir botão de desativar/excluir na gestão de Redes',
      icon: Network,
      color: '#06b6d4',
    },
    {
      key: 'allowDeleteEstados' as const,
      label: 'Estados / UFs',
      description: 'Permitir botão de desativar/excluir na gestão de Estados',
      icon: MapPin,
      color: '#10b981',
    },
    {
      key: 'allowDeleteMunicipios' as const,
      label: 'Municípios',
      description: 'Permitir botão de desativar/excluir na gestão de Municípios',
      icon: Building2,
      color: '#f59e0b',
    },
    {
      key: 'allowDeleteEscolas' as const,
      label: 'Escolas',
      description: 'Permitir botão de desativar/excluir na gestão de Escolas',
      icon: School,
      color: '#ec4899',
    },
    {
      key: 'allowDeleteEquipe' as const,
      label: 'Equipe e Gestores',
      description: 'Permitir botão de desativar/excluir membros da equipe',
      icon: UserCheck,
      color: '#8b5cf6',
    },
  ];

  return (
    <div className="admin-page animate-fadeIn">
      {/* ═══ NAVEGAÇÃO POR TABS ═══ */}
      <div
        style={{
          display: 'flex',
          gap: 8,
          borderBottom: '2px solid var(--border-color, #e5e7eb)',
          marginBottom: 20,
        }}
      >
        <button
          onClick={() => setActiveTab('status')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            padding: '10px 18px',
            fontSize: '.85rem',
            fontWeight: 600,
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            color: activeTab === 'status' ? '#6366f1' : '#6b7280',
            borderBottom: activeTab === 'status' ? '2px solid #6366f1' : '2px solid transparent',
            marginBottom: '-2px',
            transition: 'all .15s',
          }}
        >
          <Shield size={16} /> Status
        </button>

        <button
          onClick={() => setActiveTab('dev')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            padding: '10px 18px',
            fontSize: '.85rem',
            fontWeight: 600,
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            color: activeTab === 'dev' ? '#6366f1' : '#6b7280',
            borderBottom: activeTab === 'dev' ? '2px solid #6366f1' : '2px solid transparent',
            marginBottom: '-2px',
            transition: 'all .15s',
          }}
        >
          <Code2 size={16} /> Desenvolvedor
          {settings.unlocked ? (
            <span
              style={{
                fontSize: '.68rem',
                fontWeight: 700,
                color: '#10b981',
                background: 'rgba(16,185,129,0.12)',
                padding: '2px 6px',
                borderRadius: 4,
                marginLeft: 4,
              }}
            >
              Desbloqueado
            </span>
          ) : (
            <Lock size={13} style={{ marginLeft: 4, opacity: 0.6 }} />
          )}
        </button>
      </div>

      {/* ═══ TAB 1: STATUS ═══ */}
      {activeTab === 'status' && (
        <>
          {/* HERO BANNER */}
          <div
            style={{
              background: 'linear-gradient(135deg, #1f2937 0%, #111827 40%, #0f172a 100%)',
              borderRadius: 20,
              padding: '28px 32px',
              marginBottom: 24,
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                position: 'absolute',
                top: -60,
                right: -60,
                width: 200,
                height: 200,
                borderRadius: '50%',
                background: 'rgba(99,102,241,.1)',
              }}
            />
            <div style={{ position: 'relative', zIndex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                <Settings size={18} style={{ color: '#6366f1' }} />
                <span
                  style={{
                    color: 'rgba(255,255,255,.4)',
                    fontSize: '.72rem',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '.12em',
                  }}
                >
                  Sistema
                </span>
              </div>
              <h1 style={{ color: 'white', fontSize: '1.5rem', fontWeight: 800, margin: 0 }}>
                Configurações
              </h1>
              <p style={{ color: 'rgba(255,255,255,.45)', fontSize: '.82rem', marginTop: 4 }}>
                Parâmetros globais, segurança, infraestrutura e integrações da plataforma
              </p>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                  gap: 20,
                  marginTop: 24,
                }}
              >
                {[
                  { icon: Shield, value: '5 camadas', label: 'Segurança', color: '#10b981' },
                  { icon: Server, value: '99.9%', label: 'SLA Uptime', color: '#f59e0b' },
                  { icon: Palette, value: 'Dark Mode', label: 'Tema Ativo', color: '#a78bfa' },
                  { icon: Database, value: 'PostgreSQL', label: 'Storage', color: '#38bdf8' },
                ].map((s, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                    <div
                      style={{
                        width: 44,
                        height: 44,
                        borderRadius: 14,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: `${s.color}20`,
                        color: s.color,
                        flexShrink: 0,
                      }}
                    >
                      <s.icon size={22} />
                    </div>
                    <div>
                      <div style={{ fontSize: '1.2rem', fontWeight: 900, color: 'white', lineHeight: 1 }}>
                        {s.value}
                      </div>
                      <div style={{ fontSize: '.73rem', color: 'rgba(255,255,255,.6)', fontWeight: 500 }}>
                        {s.label}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* CONFIG SECTIONS */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16 }}>
            {configs.map((section, idx) => (
              <div
                key={idx}
                style={{
                  background: 'var(--bg-card, white)',
                  borderRadius: 14,
                  border: '1px solid var(--border-color, #e5e7eb)',
                  overflow: 'hidden',
                }}
              >
                {/* Section header */}
                <div
                  style={{
                    padding: '14px 18px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    borderBottom: '1px solid var(--border-color, #e5e7eb)',
                  }}
                >
                  <div
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: 8,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: `${section.color}15`,
                      color: section.color,
                    }}
                  >
                    <section.icon size={16} />
                  </div>
                  <span style={{ fontWeight: 700, fontSize: '.9rem', color: 'var(--text-main, #111)' }}>
                    {section.title}
                  </span>
                </div>

                {/* Items */}
                <div style={{ padding: '4px 0' }}>
                  {section.items.map((item, j) => (
                    <div
                      key={j}
                      style={{
                        padding: '10px 18px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        borderBottom: j < section.items.length - 1 ? '1px solid var(--border-color, #f3f4f6)' : 'none',
                      }}
                    >
                      <div>
                        <div style={{ fontSize: '.82rem', fontWeight: 600, color: 'var(--text-main, #111)' }}>
                          {item.label}
                        </div>
                        <div style={{ fontSize: '.68rem', color: 'var(--text-muted, #9ca3af)', marginTop: 1 }}>
                          {item.desc}
                        </div>
                      </div>
                      <span
                        style={{
                          fontSize: '.78rem',
                          fontWeight: 700,
                          color: section.color,
                          padding: '3px 10px',
                          borderRadius: 6,
                          background: `${section.color}10`,
                        }}
                      >
                        {item.value}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* ═══ TAB 2: DESENVOLVEDOR ═══ */}
      {activeTab === 'dev' && (
        <div>
          {!settings.unlocked ? (
            /* Tela de desbloqueio com senha 'euler' */
            <div
              style={{
                maxWidth: 440,
                margin: '30px auto',
                background: 'var(--bg-card, white)',
                borderRadius: 16,
                border: '1px solid var(--border-color, #e5e7eb)',
                padding: 28,
                textAlign: 'center',
                boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
              }}
            >
              <div
                style={{
                  width: 52,
                  height: 52,
                  borderRadius: '50%',
                  background: 'rgba(99,102,241,0.1)',
                  color: '#6366f1',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px',
                }}
              >
                <Lock size={26} />
              </div>
              <h2 style={{ fontSize: '1.15rem', fontWeight: 700, marginBottom: 8 }}>
                Acesso ao Painel Dev
              </h2>
              <p
                style={{
                  fontSize: '.83rem',
                  color: 'var(--text-muted, #6b7280)',
                  marginBottom: 20,
                  lineHeight: 1.5,
                }}
              >
                Digite a senha de desenvolvedor para desbloquear o gerenciamento avançado de permissões de remoção de cadastros.
              </p>

              <form onSubmit={handleUnlock} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div style={{ position: 'relative' }}>
                  <input
                    className="form-input"
                    type={showPassword ? 'text' : 'password'}
                    value={devPassword}
                    onChange={(e) => setDevPassword(e.target.value)}
                    placeholder="Digite a senha dev (ex: euler)"
                    style={{ width: '100%', paddingRight: 40 }}
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    style={{
                      position: 'absolute',
                      right: 10,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: '#6b7280',
                    }}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>

                <button
                  type="submit"
                  className="btn-save"
                  style={{
                    width: '100%',
                    justifyContent: 'center',
                    padding: '10px 16px',
                    fontSize: '.85rem',
                    fontWeight: 600,
                  }}
                >
                  <Unlock size={16} /> Desbloquear Painel Dev
                </button>
              </form>
            </div>
          ) : (
            /* Conteúdo do Painel Dev desbloqueado */
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              {/* Header com indicador de estado e botão de rebloquear */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  background: 'rgba(16,185,129,0.08)',
                  border: '1px solid rgba(16,185,129,0.25)',
                  borderRadius: 12,
                  padding: '14px 18px',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <CheckCircle2 size={20} style={{ color: '#10b981' }} />
                  <div>
                    <div style={{ fontSize: '.88rem', fontWeight: 700, color: 'var(--text-main)' }}>
                      Painel Desenvolvedor Desbloqueado (Senha: euler)
                    </div>
                    <div style={{ fontSize: '.78rem', color: 'var(--text-muted)' }}>
                      As alterações efetuadas aqui controlam a exibição dos botões de remoção/exclusão em todas as telas e diálogos do sistema.
                    </div>
                  </div>
                </div>

                <button
                  onClick={() => {
                    lock();
                    toast.info('Modo Desenvolvedor bloqueado.');
                  }}
                  className="btn-cancel"
                  style={{
                    fontSize: '.78rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    whiteSpace: 'nowrap',
                  }}
                >
                  <Lock size={14} /> Bloquear
                </button>
              </div>

              {/* Card 1: Permissões de Remoção por Cadastro */}
              <div
                style={{
                  background: 'var(--bg-card, white)',
                  borderRadius: 14,
                  border: '1px solid var(--border-color, #e5e7eb)',
                  padding: 20,
                }}
              >
                <div style={{ marginBottom: 16 }}>
                  <h3 style={{ margin: '0 0 4px', fontSize: '1rem', fontWeight: 700 }}>
                    Permissões de Remoção e Desativação por Módulo
                  </h3>
                  <p style={{ margin: 0, fontSize: '.8rem', color: 'var(--text-muted, #6b7280)' }}>
                    Marque os cadastros nos quais os botões de desativar/excluir registros devem estar visíveis e habilitados nos diálogos e tabelas.
                  </p>
                </div>

                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                    gap: 12,
                  }}
                >
                  {cadastrosConfigs.map((cfg) => {
                    const enabled = settings[cfg.key];
                    const Icon = cfg.icon;
                    return (
                      <label
                        key={cfg.key}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '14px 16px',
                          borderRadius: 12,
                          background: enabled ? 'var(--bg-muted, #f8fafc)' : 'rgba(239,68,68,0.03)',
                          border: `1px solid ${enabled ? 'var(--border-color, #e5e7eb)' : 'rgba(239,68,68,0.2)'}`,
                          cursor: 'pointer',
                          transition: 'all 0.15s',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, paddingRight: 8 }}>
                          <div
                            style={{
                              width: 34,
                              height: 34,
                              borderRadius: 8,
                              background: `${cfg.color}18`,
                              color: cfg.color,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                            }}
                          >
                            <Icon size={18} />
                          </div>
                          <div>
                            <div style={{ fontSize: '.88rem', fontWeight: 700, color: 'var(--text-main)' }}>
                              {cfg.label}
                            </div>
                            <div style={{ fontSize: '.74rem', color: 'var(--text-muted)', lineHeight: 1.3 }}>
                              {cfg.description}
                            </div>
                          </div>
                        </div>

                        {/* Switch Toggle */}
                        <input
                          type="checkbox"
                          checked={enabled}
                          onChange={(e) => {
                            updateSetting(cfg.key, e.target.checked);
                            toast.success(
                              `Remoção em ${cfg.label}: ${e.target.checked ? 'Habilitada' : 'Desabilitada'}`
                            );
                          }}
                          style={{
                            width: 18,
                            height: 18,
                            accentColor: '#6366f1',
                            cursor: 'pointer',
                            flexShrink: 0,
                            marginLeft: 12,
                          }}
                        />
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Card 2: Exclusão Física Permanente (Hard Delete) */}
              <div
                style={{
                  background: 'var(--bg-card, white)',
                  borderRadius: 14,
                  border: '1px solid var(--border-color, #e5e7eb)',
                  padding: 20,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: 10,
                        background: 'rgba(239,68,68,0.1)',
                        color: '#ef4444',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Trash2 size={18} />
                    </div>
                    <div>
                      <h3 style={{ margin: '0 0 2px', fontSize: '.95rem', fontWeight: 700 }}>
                        Permitir Exclusão Física Definitiva (Hard Delete)
                      </h3>
                      <p style={{ margin: 0, fontSize: '.78rem', color: 'var(--text-muted)' }}>
                        Exibe o botão de lixeira vermelha para remoção direta no banco de dados nos cadastros suportados.
                      </p>
                    </div>
                  </div>

                  <input
                    type="checkbox"
                    checked={settings.allowHardDelete}
                    onChange={(e) => {
                      updateSetting('allowHardDelete', e.target.checked);
                      toast.success(
                        `Exclusão Física (Hard Delete): ${e.target.checked ? 'Ativada' : 'Desativada'}`
                      );
                    }}
                    style={{
                      width: 18,
                      height: 18,
                      accentColor: '#ef4444',
                      cursor: 'pointer',
                      marginLeft: 12,
                    }}
                  />
                </div>
              </div>

              {/* Card 3: Recursos Globais e Interface */}
              <div
                style={{
                  background: 'var(--bg-card, white)',
                  borderRadius: 14,
                  border: '1px solid var(--border-color, #e5e7eb)',
                  padding: 20,
                }}
              >
                <div style={{ marginBottom: 16 }}>
                  <h3 style={{ margin: '0 0 4px', fontSize: '1.95rem', fontWeight: 700 }}>
                    Recursos Globais e Elementos de Interface
                  </h3>
                  <p style={{ margin: 0, fontSize: '.8rem', color: 'var(--text-muted, #6b7280)' }}>
                    Controle de forma dinâmica a exibição de componentes da interface, menus laterais e o fluxo de guias de boas-vindas.
                  </p>
                </div>

                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                    gap: 12,
                  }}
                >
                  {/* Exibir Tour Interativo */}
                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '14px 16px',
                      borderRadius: 12,
                      background: settings.showTour ? 'var(--bg-muted, #f8fafc)' : 'rgba(239,68,68,0.03)',
                      border: `1px solid ${settings.showTour ? 'var(--border-color, #e5e7eb)' : 'rgba(239,68,68,0.2)'}`,
                      cursor: 'pointer',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, paddingRight: 8 }}>
                      <div
                        style={{
                          width: 34,
                          height: 34,
                          borderRadius: 8,
                          background: 'rgba(99,102,241,0.1)',
                          color: '#6366f1',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        <Eye size={18} />
                      </div>
                      <div>
                        <div style={{ fontSize: '.88rem', fontWeight: 700, color: 'var(--text-main)' }}>
                          Exibir Tour Interativo
                        </div>
                        <div style={{ fontSize: '.74rem', color: 'var(--text-muted)', lineHeight: 1.3 }}>
                          Disponibiliza o sistema de guias/treinamentos passo a passo.
                        </div>
                      </div>
                    </div>

                    <input
                      type="checkbox"
                      checked={settings.showTour}
                      onChange={(e) => {
                        updateSetting('showTour', e.target.checked);
                        toast.success(
                          `Tour Interativo: ${e.target.checked ? 'Habilitado' : 'Desabilitado'}`
                        );
                      }}
                      style={{
                        width: 18,
                        height: 18,
                        accentColor: '#6366f1',
                        cursor: 'pointer',
                        flexShrink: 0,
                        marginLeft: 12,
                      }}
                    />
                  </label>

                  {/* Auto-Iniciar Tour */}
                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '14px 16px',
                      borderRadius: 12,
                      background: settings.autoStartTour ? 'var(--bg-muted, #f8fafc)' : 'rgba(239,68,68,0.03)',
                      border: `1px solid ${settings.autoStartTour ? 'var(--border-color, #e5e7eb)' : 'rgba(239,68,68,0.2)'}`,
                      cursor: 'pointer',
                      opacity: settings.showTour ? 1 : 0.6,
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, paddingRight: 8 }}>
                      <div
                        style={{
                          width: 34,
                          height: 34,
                          borderRadius: 8,
                          background: 'rgba(59,130,246,0.1)',
                          color: '#3b82f6',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        <Clock size={18} />
                      </div>
                      <div>
                        <div style={{ fontSize: '.88rem', fontWeight: 700, color: 'var(--text-main)' }}>
                          Auto-Iniciar Tour
                        </div>
                        <div style={{ fontSize: '.74rem', color: 'var(--text-muted)', lineHeight: 1.3 }}>
                          Inicia o tour de forma automática no primeiro acesso do usuário.
                        </div>
                      </div>
                    </div>

                    <input
                      type="checkbox"
                      checked={settings.autoStartTour}
                      disabled={!settings.showTour}
                      onChange={(e) => {
                        updateSetting('autoStartTour', e.target.checked);
                        toast.success(
                          `Auto-Iniciar Tour: ${e.target.checked ? 'Ativado' : 'Desativado'}`
                        );
                      }}
                      style={{
                        width: 18,
                        height: 18,
                        accentColor: '#3b82f6',
                        cursor: settings.showTour ? 'pointer' : 'not-allowed',
                        flexShrink: 0,
                        marginLeft: 12,
                      }}
                    />
                  </label>

                  {/* Ícone de Ajuda no Header */}
                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '14px 16px',
                      borderRadius: 12,
                      background: settings.showHelpIcon ? 'var(--bg-muted, #f8fafc)' : 'rgba(239,68,68,0.03)',
                      border: `1px solid ${settings.showHelpIcon ? 'var(--border-color, #e5e7eb)' : 'rgba(239,68,68,0.2)'}`,
                      cursor: 'pointer',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, paddingRight: 8 }}>
                      <div
                        style={{
                          width: 34,
                          height: 34,
                          borderRadius: 8,
                          background: 'rgba(245,158,11,0.1)',
                          color: '#f59e0b',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        <HelpCircle size={18} />
                      </div>
                      <div>
                        <div style={{ fontSize: '.88rem', fontWeight: 700, color: 'var(--text-main)' }}>
                          Ícone de Ajuda (?)
                        </div>
                        <div style={{ fontSize: '.74rem', color: 'var(--text-muted)', lineHeight: 1.3 }}>
                          Exibe o botão de ponto de interrogação no cabeçalho do sistema.
                        </div>
                      </div>
                    </div>

                    <input
                      type="checkbox"
                      checked={settings.showHelpIcon}
                      onChange={(e) => {
                        updateSetting('showHelpIcon', e.target.checked);
                        toast.success(
                          `Ícone de Ajuda (?): ${e.target.checked ? 'Exibido' : 'Ocultado'}`
                        );
                      }}
                      style={{
                        width: 18,
                        height: 18,
                        accentColor: '#f59e0b',
                        cursor: 'pointer',
                        flexShrink: 0,
                        marginLeft: 12,
                      }}
                    />
                  </label>

                  {/* Menu Video Aulas */}
                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '14px 16px',
                      borderRadius: 12,
                      background: settings.showVideoAulas ? 'var(--bg-muted, #f8fafc)' : 'rgba(239,68,68,0.03)',
                      border: `1px solid ${settings.showVideoAulas ? 'var(--border-color, #e5e7eb)' : 'rgba(239,68,68,0.2)'}`,
                      cursor: 'pointer',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, paddingRight: 8 }}>
                      <div
                        style={{
                          width: 34,
                          height: 34,
                          borderRadius: 8,
                          background: 'rgba(16,185,129,0.1)',
                          color: '#10b981',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        <GraduationCap size={18} />
                      </div>
                      <div>
                        <div style={{ fontSize: '.88rem', fontWeight: 700, color: 'var(--text-main)' }}>
                          Menu Video Aulas
                        </div>
                        <div style={{ fontSize: '.74rem', color: 'var(--text-muted)', lineHeight: 1.3 }}>
                          Exibe o menu lateral de Video Aulas EAD para os perfis.
                        </div>
                      </div>
                    </div>

                    <input
                      type="checkbox"
                      checked={settings.showVideoAulas}
                      onChange={(e) => {
                        updateSetting('showVideoAulas', e.target.checked);
                        toast.success(
                          `Menu Video Aulas: ${e.target.checked ? 'Exibido' : 'Ocultado'}`
                        );
                      }}
                      style={{
                        width: 18,
                        height: 18,
                        accentColor: '#10b981',
                        cursor: 'pointer',
                        flexShrink: 0,
                        marginLeft: 12,
                      }}
                    />
                  </label>

                  {/* Menu Simulador */}
                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '14px 16px',
                      borderRadius: 12,
                      background: settings.showSimulador ? 'var(--bg-muted, #f8fafc)' : 'rgba(239,68,68,0.03)',
                      border: `1px solid ${settings.showSimulador ? 'var(--border-color, #e5e7eb)' : 'rgba(239,68,68,0.2)'}`,
                      cursor: 'pointer',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, paddingRight: 8 }}>
                      <div
                        style={{
                          width: 34,
                          height: 34,
                          borderRadius: 8,
                          background: 'rgba(6,182,212,0.1)',
                          color: '#06b6d4',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        <Rocket size={18} />
                      </div>
                      <div>
                        <div style={{ fontSize: '.88rem', fontWeight: 700, color: 'var(--text-main)' }}>
                          Menu Simulador
                        </div>
                        <div style={{ fontSize: '.74rem', color: 'var(--text-muted)', lineHeight: 1.3 }}>
                          Exibe o menu lateral de Simulador na conta Master.
                        </div>
                      </div>
                    </div>

                    <input
                      type="checkbox"
                      checked={settings.showSimulador}
                      onChange={(e) => {
                        updateSetting('showSimulador', e.target.checked);
                        toast.success(
                          `Menu Simulador: ${e.target.checked ? 'Exibido' : 'Ocultado'}`
                        );
                      }}
                      style={{
                        width: 18,
                        height: 18,
                        accentColor: '#06b6d4',
                        cursor: 'pointer',
                        flexShrink: 0,
                        marginLeft: 12,
                      }}
                    />
                  </label>
                </div>
              </div>

              {/* Botão de resetar padrões */}
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => {
                    resetDefaults();
                    toast.info('Configurações do desenvolvedor restauradas para os padrões.');
                  }}
                  className="btn-cancel"
                  style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '.8rem' }}
                >
                  <RotateCcw size={14} /> Restaurar Configurações Padrão
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
